export const useImageLoading = () => {
  /**
   * Determina se uma imagem deve usar lazy loading baseado na sua posição
   * @param index - Índice do item na lista
   * @param totalAboveFold - Quantidade de itens visíveis acima da dobra
   * @returns 'lazy' | 'eager'
   */
  const getLoadingStrategy = (
    index: number,
    totalAboveFold: number = 6,
  ): 'lazy' | 'eager' => {
    return index < totalAboveFold ? 'eager' : 'lazy';
  };

  /**
   * Calcula quantos itens cabem na dobra baseado no tamanho da tela (REATIVO)
   * @param itemsPerRow - Objeto com quantidade de itens por breakpoint
   * @returns Computed number de itens visíveis na dobra
   */
  const calculateItemsAboveFold = (
    itemsPerRow: {
      mobile?: number;
      tablet?: number;
      desktop?: number;
    } = {},
  ) => {
    const { mobile = 3, tablet = 4, desktop = 6 } = itemsPerRow;

    // Usar composable do layout para ter reatividade
    const { isMobile, isTablet } = useLayout();

    return computed(() => {
      // Mobile: < 768px - 2 linhas visíveis
      if (isMobile.value) {
        return mobile * 2;
      }

      // Tablet: 768px - 1024px - 2 linhas visíveis
      if (isTablet.value) {
        return tablet * 2;
      }

      // Desktop: >= 1024px - 2 linhas visíveis (melhor para UX e evitar CLS)
      return desktop * 2;
    });
  };

  /**
   * Versão não-reativa para usar em SSR ou quando reatividade não é necessária
   * SEMPRE assume mobile-first para SSR (melhor performance inicial)
   */
  const calculateItemsAboveFoldStatic = (
    itemsPerRow: {
      mobile?: number;
      tablet?: number;
      desktop?: number;
    } = {},
  ): number => {
    const { mobile = 3, tablet = 4, desktop = 6 } = itemsPerRow;

    if (process.client) {
      const width = window.innerWidth;

      // Mobile: < 768px
      if (width < 768) {
        return mobile * 2; // 2 linhas visíveis
      }

      // Tablet: 768px - 1024px
      if (width < 1024) {
        return tablet * 2; // 2 linhas visíveis
      }

      // Desktop: >= 1024px
      return desktop * 2; // 2 linhas visíveis (melhor para UX e evitar CLS)
    }

    // SSR: assume mobile-first (menos itens eager = melhor performance)
    return mobile * 2;
  };

  return {
    getLoadingStrategy,
    calculateItemsAboveFold,
    calculateItemsAboveFoldStatic,
  };
};
